import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Folder, File, ArrowLeft, Download, Eye, Edit, Trash2,
  ChevronRight, FileText, FileCode, FileImage
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FileManager = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    loadFiles(currentPath);
  }, [currentPath, projectId]);

  const loadFiles = async (path) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API}/chat/project`,
        {
          session_id: `filemanager-${Date.now()}`,
          content: `Покажи список файлов в директории ${path || '/'}`,
          project_id: projectId
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      // Парсим ответ агента
      const metadata = response.data.metadata;
      if (metadata?.actions_executed) {
        const listAction = metadata.actions_executed.find(a => a.tool === 'list_files');
        if (listAction?.result) {
          const result = JSON.parse(listAction.result.replace(/'/g, '"'));
          setFiles(result.files || []);
        }
      }
    } catch (error) {
      console.error('Error loading files:', error);
      toast.error('Ошибка загрузки файлов');
    } finally {
      setLoading(false);
    }
  };

  const readFile = async (filePath) => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API}/chat/project`,
        {
          session_id: `filemanager-${Date.now()}`,
          content: `Прочитай файл ${filePath}`,
          project_id: projectId
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      const metadata = response.data.metadata;
      if (metadata?.actions_executed) {
        const readAction = metadata.actions_executed.find(a => a.tool === 'read_file');
        if (readAction?.result) {
          const result = JSON.parse(readAction.result.replace(/'/g, '"'));
          setFileContent(result.content || '');
          setShowPreview(true);
        }
      }
    } catch (error) {
      console.error('Error reading file:', error);
      toast.error('Ошибка чтения файла');
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['js', 'jsx', 'ts', 'tsx', 'py', 'java', 'cpp'].includes(ext)) {
      return <FileCode className="w-5 h-5 text-blue-400" />;
    } else if (['png', 'jpg', 'jpeg', 'gif', 'svg'].includes(ext)) {
      return <FileImage className="w-5 h-5 text-green-400" />;
    } else if (['txt', 'md', 'json', 'xml', 'csv'].includes(ext)) {
      return <FileText className="w-5 h-5 text-yellow-400" />;
    }
    return <File className="w-5 h-5 text-gray-400" />;
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gray-900/50 backdrop-blur">
        <div className="flex items-center gap-3">
          <Button
            onClick={() => navigate(`/chat?project=${projectId}`)}
            variant="ghost"
            size="icon"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h2 className="text-xl font-bold text-white">Файлы проекта</h2>
        </div>
        
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-gray-400">
          <span className="cursor-pointer hover:text-white" onClick={() => setCurrentPath('')}>
            {projectId}
          </span>
          {currentPath && (
            <>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">{currentPath}</span>
            </>
          )}
        </div>
      </div>

      {/* File List */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500" />
          </div>
        ) : (
          <div className="grid gap-2">
            {files.map((file, idx) => (
              <div
                key={idx}
                onClick={() => {
                  if (file.is_directory) {
                    setCurrentPath(file.path);
                  } else {
                    setSelectedFile(file);
                    readFile(file.path);
                  }
                }}
                className="flex items-center gap-3 p-3 rounded-lg border border-white/10 hover:bg-gray-800/50 cursor-pointer transition"
              >
                {file.is_directory ? (
                  <Folder className="w-5 h-5 text-violet-400" />
                ) : (
                  getFileIcon(file.name)
                )}
                <span className="flex-1 text-white">{file.name}</span>
                {!file.is_directory && (
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        readFile(file.path);
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* File Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-white/20 rounded-2xl p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">{selectedFile?.name}</h3>
              <Button
                onClick={() => setShowPreview(false)}
                variant="ghost"
                size="icon"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </div>
            <pre className="bg-gray-950 p-4 rounded-lg overflow-x-auto text-sm text-gray-300 font-mono">
              {fileContent}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileManager;
